import { useEffect, useMemo, useRef } from "react";
import L, { type LatLngExpression } from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

import { interpolatePoint, type TimedPoint } from "@/lib/replay-core";
import { colorForLap, type LapPassTimes } from "@/lib/lap-tracking";

// Leaflet resolves its default marker icons relative to the CSS file, which
// breaks in Vite production builds. Set explicit URLs so the bundler hashes
// and emits the assets correctly.
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

interface RouteLayer {
  path: number[][]; // [[lat, lng], ...]
  color: string;
  label?: string;
  points?: TimedPoint[];
}

interface Structure {
  latitude?: number;
  longitude?: number;
  size_m?: number;
  structure_kind?: string;
  source_structure_id?: number | string;
  id?: number | string;
}

interface HomePoint {
  latitude: number;
  longitude: number;
  label?: string;
}

interface RouteMapProps {
  routes: RouteLayer[];
  averageRoute?: number[][];
  structures?: Structure[];
  homePoint?: HomePoint | null;
  height?: number | string;
  progressElapsed?: number;
  /** Per-checkpoint pass times to colour gates by lap. */
  lapPasses?: LapPassTimes;
}

const CHECKPOINT_PENDING_COLOR = "#FF6B1A";

function getStructureKey(structure: Structure): string | number | null {
  return structure.source_structure_id ?? structure.id ?? null;
}

function getStructureRadius(structure: Structure): number {
  return Math.max(4, Number(structure.size_m || 20) / 2);
}

export function RouteMap({
  routes,
  averageRoute,
  structures,
  homePoint,
  height = 420,
  progressElapsed,
  lapPasses,
}: RouteMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layersRef = useRef<L.Layer[]>([]);
  const progressMarkersRef = useRef<L.CircleMarker[]>([]);
  const progressPolylinesRef = useRef<L.Polyline[]>([]);
  const checkpointLayersRef = useRef<Map<string | number, L.Circle>>(new Map());

  const allPoints = useMemo(() => {
    const points: [number, number][] = [];
    routes.forEach((r) =>
      r.path.forEach((p) => {
        if (Array.isArray(p) && p.length >= 2) points.push([p[0], p[1]]);
      }),
    );
    if (averageRoute) {
      averageRoute.forEach((p) => {
        if (Array.isArray(p) && p.length >= 2) points.push([p[0], p[1]]);
      });
    }
    if (homePoint) points.push([homePoint.latitude, homePoint.longitude]);
    return points;
  }, [routes, averageRoute, homePoint]);

  useEffect(() => {
    if (!containerRef.current) return;
    if (mapRef.current) return;
    const map = L.map(containerRef.current, {
      center: [0, 0],
      zoom: 2,
      worldCopyJump: true,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap contributors",
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Rebuild static layers
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    layersRef.current.forEach((l) => map.removeLayer(l));
    layersRef.current = [];
    progressMarkersRef.current.forEach((m) => map.removeLayer(m));
    progressMarkersRef.current = [];
    progressPolylinesRef.current.forEach((m) => map.removeLayer(m));
    progressPolylinesRef.current = [];
    checkpointLayersRef.current.forEach((layer) => map.removeLayer(layer));
    checkpointLayersRef.current.clear();

    const multipleRoutes = routes.length > 1;
    routes.forEach((route) => {
      if (!route.path.length) return;
      // Single-route mode keeps the desaturated tactical blue preview so the
      // bright orange flown segment reads as "covered ground". In multi-route
      // (compare) mode each route's preview uses its own colour so the two
      // flights are visually distinguishable even before playback starts.
      const backgroundColor = multipleRoutes ? route.color : "#5A7FA8";
      const polyline = L.polyline(route.path as LatLngExpression[], {
        color: backgroundColor,
        weight: 3,
        opacity: multipleRoutes ? 0.55 : 0.7,
        dashArray: "4 5",
      }).addTo(map);
      layersRef.current.push(polyline);

      const start = route.path[0];
      const end = route.path[route.path.length - 1];
      if (start && start.length >= 2) {
        const marker = L.circleMarker(start as LatLngExpression, {
          radius: 6,
          color: route.color,
          fillColor: route.color,
          fillOpacity: 0.85,
        }).addTo(map);
        if (route.label) marker.bindTooltip(route.label, { direction: "top" });
        layersRef.current.push(marker);
      }
      if (end && end.length >= 2) {
        const marker = L.circleMarker(end as LatLngExpression, {
          radius: 5,
          color: "#EF4444",
          fillColor: "#EF4444",
          fillOpacity: 0.85,
        }).addTo(map);
        layersRef.current.push(marker);
      }

      if (route.points && route.points.length) {
        // Bright progressive polyline; updated below as elapsed advances
        const progressLine = L.polyline([], {
          color: route.color,
          weight: 4,
          opacity: 1,
        }).addTo(map);
        progressPolylinesRef.current.push(progressLine);

        const progressMarker = L.circleMarker(start as LatLngExpression, {
          radius: 9,
          color: "#E8ECF1",
          weight: 2,
          fillColor: route.color,
          fillOpacity: 1,
          pane: "markerPane",
        }).addTo(map);
        progressMarkersRef.current.push(progressMarker);
      }
    });

    if (averageRoute?.length) {
      const polyline = L.polyline(averageRoute as LatLngExpression[], {
        color: "#A0AAB8",
        weight: 3,
        opacity: 0.7,
        dashArray: "6 6",
      }).addTo(map);
      layersRef.current.push(polyline);
    }

    structures?.forEach((structure) => {
      if (
        structure.structure_kind !== "checkpoint_ring" ||
        structure.latitude == null ||
        structure.longitude == null
      ) {
        return;
      }
      const key = getStructureKey(structure);
      const radius = getStructureRadius(structure);
      const circle = L.circle([structure.latitude, structure.longitude], {
        radius,
        color: CHECKPOINT_PENDING_COLOR,
        weight: 2,
        fillColor: CHECKPOINT_PENDING_COLOR,
        fillOpacity: 0.1,
      }).addTo(map);
      if (key !== null) checkpointLayersRef.current.set(key, circle);
      else layersRef.current.push(circle);
    });

    if (homePoint) {
      const marker = L.marker([homePoint.latitude, homePoint.longitude], {
        title: homePoint.label ?? "Home point",
      }).addTo(map);
      if (homePoint.label) marker.bindTooltip(homePoint.label, { direction: "top" });
      layersRef.current.push(marker);
    }

    if (allPoints.length >= 2) {
      map.fitBounds(L.latLngBounds(allPoints as LatLngExpression[]), {
        padding: [24, 24],
      });
    } else if (allPoints.length === 1) {
      map.setView(allPoints[0] as LatLngExpression, 14);
    }
  }, [routes, averageRoute, structures, homePoint, allPoints]);

  // Update progress marker + bright trail as elapsed changes
  useEffect(() => {
    const markers = progressMarkersRef.current;
    const lines = progressPolylinesRef.current;
    if (!markers.length || progressElapsed === undefined) return;
    routes.forEach((route, idx) => {
      if (!route.points?.length) return;
      const marker = markers[idx];
      const line = lines[idx];
      if (!marker) return;

      const point = interpolatePoint(route.points, progressElapsed);
      if (point) marker.setLatLng([point.latitude, point.longitude]);

      if (line) {
        let cutoff = 0;
        for (let i = 0; i < route.points.length; i += 1) {
          if (route.points[i].elapsed_seconds <= progressElapsed) cutoff = i;
          else break;
        }
        const trail = route.points
          .slice(0, cutoff + 1)
          .map((p) => [p.latitude, p.longitude]) as LatLngExpression[];
        if (point) trail.push([point.latitude, point.longitude]);
        line.setLatLngs(trail);
      }
    });
  }, [progressElapsed, routes]);

  // Recolor checkpoints when lap state changes
  useEffect(() => {
    if (progressElapsed === undefined) return;
    checkpointLayersRef.current.forEach((layer, key) => {
      const passes = lapPasses?.get(key) ?? [];
      let lap = 0;
      for (const t of passes) {
        if (t <= progressElapsed) lap += 1;
        else break;
      }
      const color = lap > 0 ? colorForLap(lap) : CHECKPOINT_PENDING_COLOR;
      layer.setStyle({
        color,
        fillColor: color,
        fillOpacity: lap > 0 ? 0.35 : 0.1,
      });
    });
  }, [lapPasses, progressElapsed]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      mapRef.current?.invalidateSize();
    }, 100);
    return () => window.clearTimeout(timer);
  }, [height]);

  return <div ref={containerRef} style={{ height, width: "100%" }} className="route-map" />;
}
